import React from 'react'
import { Table } from 'react-bootstrap'
import { LinkContainer } from 'react-router-bootstrap'
import { connect } from "react-redux"
import ReactTooltip from "react-tooltip"
import _ from 'lodash'
import { changeApplication } from 'actions/rootActions'
import { simpleDispatchProps } from 'actions/actionUtils'
import numeral from 'numeral'

const imagePlaceholder = '/assets/images/image_placeholder.png'

@connect((store) => ({
  applicationId: store.root.applicationId,
}), (dispatch) => simpleDispatchProps(dispatch, {
  changeApplication,
}))

export default class ListTable extends React.Component {
  constructor(props) {
    super(props)
    const rowKeys = props.rowKeyField ? props.data.map(el => el[props.rowKeyField]) : props.data.map((el, index) => index)
    this.state = {
      showExtra: Object.assign({}, ...rowKeys.map(rowKey => ({ [rowKey]: false })))
    }
    this.sortData = this.sortData.bind(this)
    this.sort = this.sort.bind(this)
    this.contentWrapper = this.contentWrapper.bind(this)
    this.toggleExtra = this.toggleExtra.bind(this)
  }

  /**
   * Sort items based on sort by field and sort order
   * @param {Array} data items to sort
   * @param {String} sortBy sort by field
   * @param {Boolean} sortAsc true for asc and false for desc
   */
  sortData(sortBy, sortAsc, data) {
    if (!sortBy) return data
    return data.sort((a, b) => {
      if (a[sortBy] === null || b[sortBy] === null) {
        return a[sortBy] !== null ? -1 : (b[sortBy] !== null ? 1 : 0)
      }
      return (sortAsc ? 1 : -1) * (a[sortBy] > b[sortBy] ? 1 : (a[sortBy] < b[sortBy] ? -1 : 0))
    })
  }

  /**
   * Callback function for when the header's sort button is clocked (needs to be migrated to redux)
   * @param {String} sortBy sort by field of the clicked button
   */
  sort(sortBy) {
    const { defaultSortAscFields = [], sortWithinComponent } = this.props
    const newSortAsc = sortBy === this.props.sortBy ? !this.props.sortAsc : defaultSortAscFields.includes(sortBy)
    const newData = sortWithinComponent ? this.sortData(sortBy, newSortAsc, this.props.data) : null
    if (this.props.onSort) {
      this.props.onSort(sortBy, newSortAsc, newData)
    }
  }

  /**
   * Generate table cell instance for an element-field pair
   * @param {Number} index element index
   * @param {Object} el the current data element of table row
   * @param {String} key the data field of the table cell
   * @param {Object} formatters the formatters of all fields
   * @param {Object} linkTo the dictionary of link to (adding link to the cell using value of another field)
   * @param {Object} tooltipTo the dictionary of tooltip to (adding tooltip to the cell using value of another field)
   * @param {Object} subContentFormatters the formatters of sub content
   * @param {Object} redirectToPath specifies which router path will be redirected to when clicking on a cell, example: { rank: 'competitors' }
   * @param {Object} redirectToSearch specifies the what url query will be changed to when clicking on a cell, example: { keyword: 'q=iphone'}
   * @param {Object} tdStyles the key-style dict, example: { image_url: { text-align: 'center' }}
   * @param {Array} imageFields array of image fields
   */
  contentWrapper(rowKey, el, key, formatters = {}, formats = {}, linkTo, tooltipTo, subContentFormatters, subContentStyles, redirectToPath, redirectToSearch, tdStyles, imageFields = [], tooltipKey = {}, rowSpan) {
    let instance, content, subContent = null

    if (imageFields.includes(key)) {
      content = <img style={{ margin: 'auto' }} src={el[key] || imagePlaceholder} loading="lazy" alt="" />
    }
    else if (formatters[key]) {
      content = formatters[key](el[key], el.currency || el.currency_id, el)
    }
    else if (formats[key]) {
      content = numeral(el[key]).format(formats[key])
    }
    else {
      content = (el[key] || '').toString() || 'N/A'
    }

    if (subContentFormatters[key]) {
      subContent = <i className="sub-content" style={subContentStyles[key] || {display: 'block'}}>{subContentFormatters[key](el)}</i>
    }
    if (linkTo[key] && el[linkTo[key]]) {
      content = <a target="_blank" href={el[linkTo[key]]}>{content}</a>
    }
    if (redirectToPath[key] && redirectToSearch[key]) {
      content = (
        <LinkContainer
          to={{ pathname: redirectToPath[key], search: redirectToSearch[key](el) }}
          onClick={() => this.props.changeApplication(this.props.applicationId, redirectToPath[key].substr(1))}
        >
          <span>{content || 'N/A'}</span>
        </LinkContainer>
      )
    }
    instance = subContent ? <div>{content}{subContent}</div> : <div>{content}</div>

    if (tooltipKey[key]) {
      if (el[tooltipKey[key]]) {
        if (Object.keys(el[tooltipKey[key]]).length >= 1) {
          instance = (
            <>
              <div data-tip data-for={`tooltip-primary-row-${rowKey}-col-${key}`}>
                {instance}
              </div>
              <ReactTooltip id={`tooltip-primary-row-${rowKey}-col-${key}`}>
                {Object.keys(el[tooltipKey[key]]).map((conversion, index) => (
                  <div key={index} style={{ maxWidth: 270, maxHeight: 270 }}>
                    {_.startCase(conversion.toLowerCase())}: {numeral(el[tooltipKey[key]][conversion]).format('0,0')}
                  </div>
                ))}
              </ReactTooltip>
            </>
          )
        }
      }
    }

    return (
      <td rowSpan={rowSpan} key={`data-row-${rowKey}-col-${key}`} style={tdStyles[key]} title={tooltipTo[key] ? el[tooltipTo[key]] : null}>
        {instance}
      </td>
    )
  }

  renderDataRows = () => {
    const {
      keys,
      formatters,
      formats,
      linkTo = {},
      tooltipTo = {},
      redirectToPath = {},
      redirectToSearch = null,
      subContentFormatters = {},
      subContentStyles = {},
      data,
      extraChartFor,
      extraChartComponent,
      tdStyles = {},
      rowKeyField,
      imageFields,
      tooltipKey,
      appliedFieldValueDict,
    } = this.props
    const { showExtra } = this.state
    const dataRows = []
    const appliedFields = Object.keys(appliedFieldValueDict || {})

    data.forEach((el, index) => {
      if (!el || typeof el !== 'object') return
      const rowKey = rowKeyField ? el[rowKeyField] : index
      dataRows.push(
        <tr key={`data-row-${rowKey}`}>
          {
            extraChartFor ? (
              <td className="center" onClick={() => this.toggleExtra(rowKey)} title={showExtra[rowKey] ? '' : 'More charts'}>
                <span style={{width: 20, padding: 0}} className={`btn center ${(el.pendingFields || el.failedFields) ? 'disabled' : ''}`}>
                  <span className={`fa ${showExtra[rowKey] ? 'fa-chevron-down' : 'fa-chevron-right'}`}/>
                </span>
              </td>
              ) : null
          }
          {keys.map(key => {
            let rowSpan = 1
            if (appliedFields.includes(key) && (appliedFields.indexOf(key) < appliedFields.length - 1)) {
              const scanFields = appliedFields.slice(0, appliedFields.indexOf(key) + 1)
              const findFunction = (entry) => {
                return scanFields.reduce((acc, f) => {
                  return acc && (entry[f] === el[f])
                }, true)
              }
              const keyRowIndex = _.findIndex(data, findFunction)
              if (keyRowIndex === index) {
                // Calculate row span
                rowSpan = data.filter(findFunction).length
              }
              else return null
            }
            if (Array.isArray(el.pendingFields) && el.pendingFields.includes(key)) {
              return (
                <td rowSpan={rowSpan} className="center" key={`data-row-${rowKey}-col-${key}`} title="Loading...">
                  <div className="loading-line" />
                </td>
              )
            }
            else if (Array.isArray(el.failedFields) && el.failedFields.includes(key)) {
              return (
                <td rowSpan={rowSpan} className="center" key={`data-row-${rowKey}-col-${key}`} title="Failed to load">
                  <span className="fa fa-frown" />
                </td>
              )
            }
            else {
              return this.contentWrapper(rowKey, el, key, formatters, formats, linkTo, tooltipTo, subContentFormatters, subContentStyles, redirectToPath, redirectToSearch, tdStyles, imageFields, tooltipKey, rowSpan)
            }
          })}
          { this.props.extraColumnForEditMode ? <td key="add-column-cell" className="add-column-cell" /> : null }
        </tr>
      )
      if (extraChartFor && el[extraChartFor] && showExtra[rowKey]) {
        dataRows.push(
          <tr key={`data-row-extra-${rowKey}`}>
            <td colSpan={keys.length + 1}>
              { React.createElement(extraChartComponent, { data: el[extraChartFor], currency: el.currency || el.currency_id })}
            </td>
          </tr>
        )
      }
    })
    return dataRows
  }

  toggleExtra(rowKey) {
    const { showExtra } = this.state
    this.setState({
      showExtra: { ...showExtra, [rowKey]: !showExtra[rowKey] }
    })
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.fetching && !nextProps.fetching) {
      const rowKeys = nextProps.rowKeyField ? nextProps.data.map(el => el[nextProps.rowKeyField]) : nextProps.data.map((el, index) => index)
      this.setState({ showExtra: Object.assign({}, ...rowKeys.map(rowKey => ({ [rowKey]: false })))})
    }
  }

  UNSAFE_componentWillMount() {
    const { data, sortBy, sortAsc, onSort, sortWithinComponent } = this.props
    if (onSort && sortWithinComponent) onSort(this.sortData(sortBy, sortAsc, data))
  }

  render() {
    const {
      keys,
      names,
      formatters,
      formats,
      linkTo = {},
      tooltipTo = {},
      redirectToPath = {},
      redirectToSearch = null,
      subContentFormatters = {},
      subContentStyles = {},
      sortableFields,
      className,
      sortBy,
      sortAsc,
      data,
      extraChartFor,
      extraChartComponent,
      tdStyles = {},
      rowKeyField,
      imageFields,
      tooltipKey,
      thead,
      extraColumnForEditMode,
    } = this.props
    const { showExtra } = this.state

    const dataRows = this.renderDataRows()
    const { emptyMessage } = this.props
    const colSpan = (extraChartFor ? 1 : 0) + keys.length + (extraColumnForEditMode ? 1 : 0)
    const showEmptyRow = emptyMessage && (!data || !data.length)
    const emptyStateRow = showEmptyRow ? (
      <tr key="empty-state-row">
        <td colSpan={colSpan} className="empty-table-message-cell">
          <div className="empty-message">{emptyMessage}</div>
        </td>
      </tr>
    ) : null

    const defaultThead = (
      <thead>
        <tr>
          { extraChartFor ? <td>&nbsp;</td> : null }
          {
            keys.map(key => (
              (sortableFields && sortableFields.indexOf(key) >= 0) ?
              (
                <th className="sortable" onClick={() => this.sort(key)} key={`header-${key}`}>
                  <span>
                    <span className="header-text">{names[key]}</span>
                    <span className={sortBy === key ? (sortAsc ? 'fa fa-caret-up' : 'fa fa-caret-down') : ''}/>
                  </span>
                </th>
              ) :
              <th key={`header-${key}`}>{names[key]}</th>
            ))
          }
        </tr>
      </thead>
    )

    return (
      <div className={className || "list-table"}>
        <Table striped bordered hover responsive>
          { thead != null ? thead : defaultThead }
          <tbody>
            { dataRows.length ? dataRows : emptyStateRow }
          </tbody>
        </Table>
      </div>
    )
  }
}
